import * as React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import {useContext, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";

import { AuthContext } from '../../auth/providers/AuthProvider';
import { CsrfContext } from '../../../providers/CsrfProvider';
import {Link} from "react-router-dom";
import {ThemeContext} from "styled-components";
import ButtonIcon from './Buttons/ButtonIcon';
import ButtonMenu from './Buttons/ButtonMenu';
import ButtonDivider from './Buttons/ButtonDivider';
import Typography from "@mui/material/Typography";
import {performLogout} from "../../../utils/auth/LogoutHandler";
import { useTheme } from '../../../styles/ThemeProvider';
import IconDay from '../../../assets/Images/Icons/IconDay';
import IconNight from '../../../assets/Images/Icons/IconNight';
import Box from '@mui/material/Box';
import WithIcons from './Wrappers/WithIcons';

const AppMenu = React.memo(() => {
    const theme = useContext(ThemeContext)
    const { authClaims, setAuthClaims } = useContext(AuthContext);
    const { csrfToken } = useContext(CsrfContext);
    const navigate = useNavigate();
    const { toggleTheme } = useTheme();
    const buttonRef = useRef(null);

    const handleLogout = async (e) => {
        e.preventDefault();
        await performLogout(csrfToken, setAuthClaims, (path, state) => navigate(path, { state }));
    };

    const handleThemeToggle = (e) => {
        e.preventDefault();
        toggleTheme();
    };

    // Add CSS override for menu positioning
    React.useEffect(() => {
        const styleId = 'app-menu-position-override';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                #popup-menu > div.MuiPaper-root.MuiMenu-paper {
                    top: 48px !important;
                }
            `;
            document.head.appendChild(style);
        }
        return () => {
            const style = document.getElementById(styleId);
            if (style) {
                style.remove();
            }
        };
    }, []);

    return (
        <PopupState variant="popover" popupId="popup-menu">
            {(popupState) => {
                const bindTriggerProps = bindTrigger(popupState);
                const bindMenuProps = bindMenu(popupState);

                return (
                    <>
                        <ButtonIcon
                            ref={buttonRef}
                            {...bindTriggerProps}
                            baseOpacity={0.85}
                            hoverOpacity={1}
                            pressedOpacity={0.5}
                            color={theme.colors.secondary}
                            sx={{
                                pointerEvents: 'auto !important',
                                zIndex: '1251 !important',
                                position: 'relative',
                            }}
                        >
                            <Typography fontWeight={800}>ooo</Typography>
                        </ButtonIcon>
                    <Menu
                        {...bindMenuProps}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        TransitionProps={{ 
                            timeout: 100,
                            onEntered: () => {
                                // Force menu to align with header bottom (48px)
                                const forcePosition = () => {
                                    // Try multiple selectors to find the menu
                                    const selectors = [
                                        '#popup-menu > div.MuiPaper-root.MuiMenu-paper',
                                        '#popup-menu .MuiMenu-paper',
                                        '[id="popup-menu"] .MuiPaper-root'
                                    ];
                                    
                                    let menuPaper = null;
                                    for (const selector of selectors) {
                                        menuPaper = document.querySelector(selector);
                                        if (menuPaper) break;
                                    }
                                    
                                    if (menuPaper) {
                                        // Remove any existing transform
                                        menuPaper.style.removeProperty('transform');
                                        // Set top position directly
                                        menuPaper.style.setProperty('top', '48px', 'important');
                                        // Also set position if needed
                                        const computedStyle = window.getComputedStyle(menuPaper);
                                        if (computedStyle.position !== 'fixed' && computedStyle.position !== 'absolute') {
                                            menuPaper.style.setProperty('position', 'fixed', 'important');
                                        }
                                    }
                                };
                                
                                // Try multiple times with delays
                                forcePosition();
                                setTimeout(forcePosition, 0);
                                setTimeout(forcePosition, 10);
                                setTimeout(forcePosition, 50);
                                setTimeout(forcePosition, 100);
                            }
                        }}
                        PaperProps={{ sx: { right: 0, left: 'auto', mt: 0, mr: 0,
                                boxShadow: '-6px 6px 18px rgba(0, 0, 0, 0.22), -3px 3px 8px rgba(0, 0, 0, 0.18)',
                                backgroundColor: theme.colors.menuBg,
                                backgroundImage: 'none',
                                '&.MuiPaper-root': {
                                    backgroundColor: theme.colors.menuBg,
                                },
                                color: theme.colors.menuText, width: 150 } }}
                        MenuListProps={{ dense: true }}
                    >
                        { ! authClaims
                            ? [
                                <ButtonMenu
                                    key="login"
                                    component={Link}
                                    to={{ pathname: '/auth', state: { initialTab: 'login' } }}
                                    onClick={popupState.close}
                                    sx={{ pl: 0, pr: 0 }}
                                >
                                    <WithIcons>
                                        Login
                                    </WithIcons>
                                </ButtonMenu>,
                                <ButtonDivider key="divider-guest" />,
                                <ButtonMenu
                                    key="join"
                                    component={Link}
                                    to={{ pathname: '/auth', state: { initialTab: 'signup' } }}
                                    onClick={popupState.close}
                                    sx={{ pl: 0, pr: 0 }}
                                >
                                    <WithIcons>
                                        Join
                                    </WithIcons>
                                </ButtonMenu>,
                              ]
                            : [
                                <ButtonMenu
                                    key="logout"
                                    onClick={(e) => {
                                        popupState.close();
                                        handleLogout(e);
                                    }}
                                    sx={{ pl: 0, pr: 0 }}
                                >
                                    <WithIcons>
                                        Logout
                                    </WithIcons>
                                </ButtonMenu>,
                                <ButtonDivider key="divider-user" />,
                                <ButtonMenu
                                    key="profile"
                                    component={Link}
                                    to="/profile"
                                    onClick={popupState.close}
                                    sx={{ pl: 0, pr: 0 }}
                                >
                                    <WithIcons>
                                        Profile
                                    </WithIcons>
                                </ButtonMenu>
                              ]
                        }
                        <ButtonDivider key="divider-theme" />
                        <ButtonMenu
                            key="theme-toggle"
                            onClick={(e) => {
                                handleThemeToggle(e);
                                popupState.close();
                            }}
                            sx={{ pl: 0, pr: 0 }}
                        >
                            <WithIcons
                                showLeftIcon={true}
                                leftIcon={theme.current === 'light' ? IconDay : IconNight}
                                leftIconSize={18}
                            >
                                {theme.current === 'light' ? 'Dark Mode' : 'Light Mode'}
                            </WithIcons>
                        </ButtonMenu>
                    </Menu>
                </>
                );
            }}
        </PopupState>
    );
});

export default AppMenu;
